import { Alert, Box, Button, TextField, Typography, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormik } from "formik";
import { useContext, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import * as yup from "yup";
import Layout from "../../components/layout/Layout";
import "./AddCaregiverPage.css";
import { ClientContext } from "../../state/ClientContext";
import { wasAuthFailureReported } from "../../api/authFailureMarker";
import { US_STATES } from "../../util/defs";
import { apiRequest } from "../../api/baseApi";
import LeaveWithoutSavingModal from "../../components/LeaveWithoutSavingModal";
import { addBreadcrumb, captureMessage } from "../../config/sentry";
import {
  apiErrorMessage,
  isDuplicateEmailError,
} from "../../utils/apiErrorMessage";

// #771: shown for 5xx and transport failures, where the server text is either
// absent or internal detail the admin cannot act on.
const GENERIC_INVITE_ERROR =
  "We couldn\u2019t send the invite. Check your connection and try again.";

const AddCaregiverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { refetchCaregivers } = useContext(ClientContext);

  const isFormForAdmin = location.pathname.includes("/add-admin");

  const responsibilityOptions = [
    { label: "Assisted Living Center", value: "Assisted Living Renter" },
    { label: "Behavioral Health", value: "Behavioral Health" },
    { label: "Psychiatric Professional", value: "Psychiatric Professional" },
    { label: "Group Home", value: "Group Home" },
    { label: "DO/MD Office", value: "DO/MD Office" },
    { label: "Aging Office", value: "Aging Office" },
    {
      label: "Certified Nursing Assistant",
      value: "Certified Nursing Assistant",
    },
    { label: "Licensed Practical Nurse", value: "Licensed Practical Nurse" },
    { label: "Registered Nurse", value: "Registered Nurse" },
    {
      label: "Physical or Occupational Therapist",
      value: "Physical or Occupational Therapist",
    },
    { label: "Friend or Family Member", value: "Friend or Family Member" },
    { label: "Speech Therapist", value: "Speech Therapist" },
    { label: "Support Coordinator", value: "Support Coordinator" },
    { label: "House Manager", value: "House Manager" },
    { label: "Other", value: "Other" },
  ];

  const schemaParams = {
    firstName: yup.string().required("Enter first name."),
    lastName: yup.string().required("Enter last name."),
    email: yup.string().email("Enter a valid email.").required("Enter email."),
    phone: yup
      .string()
      .matches(/^\+1\d{10}$/, "Phone number must be in the format +1XXXXXXXXXX")
      .required("Enter valid U.S phone number."),
    title: yup.string().required("Enter title."),
    responsibility: yup.string().required("Enter responsibility."),
    company: yup.string().required("Enter company name."),
    city: yup
      .string()
      .matches(
        /^[a-zA-Z ]*$/,
        "City can’t contain any special characters or numbers."
      )
      .required("Enter city."),
    state: yup.string().required("Enter state."),
    zipCode: yup
      .string()
      .matches(/^\d{5}$/, "Zip code must be 5 digits.")
      .required("Enter zip code."),
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      title: "",
      email: "",
      phone: "",
      company: "",
      responsibility: "",
      city: "",
      state: "",
      zipCode: "",
    },
    validationSchema: yup.object(schemaParams),
    onSubmit: async (values) => {
      const userType = isFormForAdmin ? "caregiver_admin" : "caregiver";
      setSubmitError("");
      // #771: breadcrumb every outcome — status and user type only, never the
      // invitee's name or email.
      const crumb = (outcome, extra = {}) =>
        addBreadcrumb("caregiver.invite.submit", {
          category: "caregiver.invite",
          level: outcome === "success" ? "info" : "warning",
          outcome,
          userType,
          ...extra,
        });

      // #782: apiRequest already captures every thrown transport failure and
      // every 5xx, tags them api_reported (baseApi.js:191-207), and withholds
      // them during a burst. Sending our own event for those duplicates the
      // report and overrides a silence the app chose deliberately. A 4xx and a
      // malformed 2xx it only breadcrumbs, and those are the ones worth their
      // own event here: they carry user_type and the admin-facing status.
      // #673: the numeric status cannot see baseApi's REACTIVE auth path,
      // which reports the failure and then falls back to RETURNING the
      // original 401 rather than throwing. The stamp on that response is the
      // only evidence. An unmarked 401 — the server's answer to a freshly
      // refreshed token — is a real authorization result and stays reportable.
      const centrallyReported = (status, response) =>
        (typeof status === "number" && status >= 500) ||
        wasAuthFailureReported(response);

      // A breadcrumb is scope only and transmits nothing on its own, so a
      // failure apiRequest did NOT report is also a captured event: status and
      // user type, no PII.
      const fail = (
        status,
        userMessage = GENERIC_INVITE_ERROR,
        alreadyReported = false
      ) => {
        crumb("failed", { status });
        if (!alreadyReported) {
          captureMessage("caregiver.invite.failed", {
            level: "warning",
            tags: { status: String(status), user_type: userType },
          });
        }
        setSubmitError(userMessage);
      };

      try {
        const response = await apiRequest("/users/invite", {
          version: "v1",
          method: "POST",
          body: {
            name: values.firstName + " " + values.lastName,
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            user_type: userType,
            title: values.title,
            company: values.company,
            city: values.city,
            state: values.state,
            phone: values.phone,
            zip_code: values.zipCode,
            responsibility: values.responsibility,
          },
        });

        const data = await response.json().catch(() => null);

        if (response.ok && !data?.user) {
          // A 2xx with an empty or unparseable body: InviteSentPage renders
          // nothing without `user`, so navigating would land on a blank page.
          fail(response.status);
          return;
        }

        if (response.ok) {
          crumb("success", {
            status: response.status,
            emailFailed: Boolean(data?.emailWarning),
          });
          refetchCaregivers();
          navigate("/invite-sent", {
            state: { user: data?.user, emailWarning: data?.emailWarning },
          });
          return;
        }

        const message = apiErrorMessage(data);
        if (isDuplicateEmailError(response.status, message)) {
          crumb("duplicate", { status: response.status });
          formik.setFieldError(
            "email",
            "This email is already assigned to another caregiver. Enter a different email."
          );
          return;
        }

        // A 4xx carries a message written for the admin (e.g. "Forbidden.
        // Admin access required."); a 5xx may carry internal detail.
        fail(
          response.status,
          response.status < 500 && message ? message : GENERIC_INVITE_ERROR,
          centrallyReported(response.status, response)
        );
      } catch (error) {
        // isNetworkFailure and isAuthFailure are how apiRequest tells a caller
        // it has already reported this one (baseApi.js:277-283, :300;
        // responseUtils forwards isAuthFailure off a stamped response).
        // Anything else thrown here is our own bug and nobody has sent it.
        fail(
          "network",
          GENERIC_INVITE_ERROR,
          error?.isNetworkFailure === true || error?.isAuthFailure === true
        );
      }
    },
  });

  const onInputChangeResponsibility = (newValue) => {
    formik.setFieldValue("responsibility", newValue.value);
  };

  return (
    <Layout isTouched={formik.dirty} showSideNav={false}>
      <div className="alerts-page-container">
        <div className="user-page-heading">
          <h1>Add new {isFormForAdmin ? "admin" : "caregiver"}</h1>
          <p>
            Invite the caregiver to register their Compass Care account. After
            registering, you can assign them to clients.
          </p>
        </div>
        <div className="add-client-container">
          <Box
            component="form"
            sx={{ "& > :not(style)": { m: 1, width: "100%" } }}
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
          >
            <h2 className="form-heading">
              <strong>Caregiver Details</strong>
            </h2>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="firstName">First Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="lastName">Last Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="text"
                  id="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="title">Title</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="title"
                  id="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="company">Company Name</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="company"
                  id="company"
                  value={formik.values.company}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.company && Boolean(formik.errors.company)
                  }
                  helperText={formik.touched.company && formik.errors.company}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="responsibility">Responsibility</label>
              </Grid>
              <Grid item size={8}>
                <Select
                  defaultValue={[]}
                  name="responsibility"
                  onChange={onInputChangeResponsibility}
                  options={responsibilityOptions}
                  placeholder="Select caregiver responsibility"
                  classNamePrefix="select"
                />
                {formik.errors.responsibility && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#d32f2f",
                      paddingLeft: "15px",
                      textAlign: "left",
                    }}
                  >
                    {formik.errors.responsibility}
                  </div>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="email">Email</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="phone">Phone Number</label>
              </Grid>
              <Grid item size={8}>
                <InputMask
                  mask="+19999999999"
                  value={formik.values.phone}
                  disabled={false}
                  maskChar=" "
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      id="phone"
                      type="tel"
                      placeholder="+1XXXXXXXXXX"
                      value={formik.values.phone}
                      error={
                        formik.touched.phone && Boolean(formik.errors.phone)
                      }
                      helperText={formik.touched.phone && formik.errors.phone}
                      fullWidth
                      required
                    />
                  )}
                </InputMask>
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="city">City</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  type="city"
                  id="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.city && Boolean(formik.errors.city)}
                  helperText={formik.touched.city && formik.errors.city}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="state">State</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="state"
                  name="state"
                  select
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.state && Boolean(formik.errors.state)}
                  helperText={formik.touched.state && formik.errors.state}
                  fullWidth
                  required
                >
                  {US_STATES.map((stateName) => (
                    <MenuItem key={stateName} value={stateName}>
                      {stateName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Grid item size={4}>
                <label htmlFor="zipCode">ZIP code</label>
              </Grid>
              <Grid item size={8}>
                <TextField
                  id="zipCode"
                  type="text"
                  value={formik.values.zipCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.zipCode && Boolean(formik.errors.zipCode)
                  }
                  helperText={formik.touched.zipCode && formik.errors.zipCode}
                  fullWidth
                  required
                />
              </Grid>
            </Grid>
          </Box>
          {submitError && (
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box></Box>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={() => {
                  if (formik.dirty) {
                    setIsLeaveModalOpen(true);
                  } else {
                    navigate(-1);
                  }
                }}
              >
                <Typography textTransform={"capitalize"}>Cancel</Typography>
              </Button>
              <Button
                variant="outlined"
                className="activity-action-btn"
                onClick={formik.handleSubmit}
                disabled={formik.isSubmitting}
              >
                <Typography textTransform={"capitalize"}>
                  {formik.isSubmitting ? "Saving\u2026" : "Save"}
                </Typography>
              </Button>
            </Box>
          </Box>
        </div>
      </div>

      <LeaveWithoutSavingModal
        isOpen={isLeaveModalOpen}
        handleClose={() => setIsLeaveModalOpen(false)}
        handleLeave={() => navigate(-1)}
      />
    </Layout>
  );
};

export default AddCaregiverPage;
